/**
 * Serialização do formulário de proposta → FormData do backend.
 *
 * Função PURA, sem React: recebe o estado do editor e devolve o multipart que
 * `POST/PUT /procurement/proposals` espera. Extraída do LicitacoesTab pra ser a
 * ÚNICA via de escrita — o editor legado (3 saves: create/update/draft) e o
 * ProposalWorkspace novo compartilham exatamente esta função, então não existe
 * caminho onde um campo é gravado por um e esquecido pelo outro.
 *
 * ⚠️ Regra do backend (DTO whitelist): campo que não está declarado no
 * Create/UpdateProposalDto é descartado em SILÊNCIO. Ao acrescentar campo aqui,
 * conferir o DTO correspondente.
 */

export type AcceptedTermsPayload = {
  termoAceitacao: boolean;
  meepp: 'Sim' | 'Não' | null;
  equidadeGenero?: 'Sim' | 'Não';
  equidadeNivel?: 'Ouro' | 'Prata' | 'Bronze';
  integridade?: 'Sim' | 'Não';
  /** Margem de preferência (CICS) — aplicada pelo robô nos itens que exigirem */
  margemPreferencia?: { paisOrigem: string; enquadramento: string };
  /** PCP: respostas de desempate (informações complementares) */
  pcpInfoComplementar?: Array<{ field: string; label: string; resposta: string }>;
};

/** Destila as declarações do Art. 60 (Lei 14.133/2021) no payload do portal. */
export function buildAcceptedTermsPayload(
  termoAceitacao: boolean,
  meepp: 'Sim' | 'Não' | null,
  declarations: Array<{ id: string; currentValue: string }>,
): AcceptedTermsPayload {
  const find = (id: string) => declarations.find((d) => d.id === id)?.currentValue;
  const equidade = find('art60_equidade');
  const integridade = find('art60_integridade');
  const nivel = find('art60_enquadramento');
  const payload: AcceptedTermsPayload = { termoAceitacao, meepp };
  if (equidade === 'Sim' || equidade === 'Não') payload.equidadeGenero = equidade;
  if (integridade === 'Sim' || integridade === 'Não') payload.integridade = integridade;
  if (equidade === 'Sim' && (nivel === 'Ouro' || nivel === 'Prata' || nivel === 'Bronze')) {
    payload.equidadeNivel = nivel;
  }
  return payload;
}

export interface ProposalFormState {
  itemId: string;
  title: string;
  description: string;
  totalValue: string;
  status: string;
  checklistNotes: string;
  validityDays: string;
  observations: string;
  conditions: string;
  representative: string;
  representativeRole: string;
  bankInfo: string;
  brand: string;
  model: string;
  valorRecorrente: boolean;
  contractMonths: string;
  /** Formato da planilha no PDF; vazio = o gerador decide pelos itens. */
  pdfLayout: string;
  /** Seção "Tributação incidente" (Simples Nacional) no PDF. */
  pdfIncluirTributacao: boolean;
}

export interface ProposalFormInput {
  form: ProposalFormState;
  items: any[];
  /** total já calculado por proposalMath (respeita chargeType/meses) */
  computedTotal: number;
  declarations: Array<{ id: string; label: string; currentValue: string }>;
  termsAccepted: boolean;
  meeppValue: 'Sim' | 'Não' | null;
  margemPaisOrigem: string;
  margemEnquadramento: string;
  pcpInfoComp: Array<{ field: string; label: string; resposta: string }>;
  /** portalOrigem efetivo (do item selecionado ou da proposta em edição) */
  portalOrigem?: string | null;
  /** item.termsDeclarations — se existe, o portal capturou termos no scrape */
  hasPortalTerms?: boolean;
  file?: File | null;
  includeItemId?: boolean;
  includeFile?: boolean;
}

export function buildProposalFormData(input: ProposalFormInput): FormData {
  const {
    form, items, computedTotal, declarations, termsAccepted, meeppValue,
    margemPaisOrigem, margemEnquadramento, pcpInfoComp, portalOrigem,
    hasPortalTerms, file, includeItemId, includeFile,
  } = input;

  const fd = new FormData();
  if (includeItemId) fd.append('itemId', form.itemId);
  fd.append('title', form.title);
  if (form.description) fd.append('description', form.description);

  const totalVal = items.length > 0 ? computedTotal : parseFloat(form.totalValue);
  if (totalVal && totalVal > 0) fd.append('totalValue', String(totalVal));

  fd.append('status', form.status);
  if (form.checklistNotes) fd.append('checklistNotes', form.checklistNotes);
  if (includeFile && file) fd.append('file', file);

  if (items.length > 0) {
    fd.append('proposalItems', JSON.stringify(items.map((it: any) => ({
      // O PUT substitui os itens inteiros: sem devolver o id do portal, salvar apagava a chave da gravação (Licitanet).
      codItem: it.codItem ?? undefined,
      itemNumber: it.itemNumber,
      description: it.description,
      descricaoDetalhada: it.descricaoDetalhada || undefined,
      quantity: it.quantity,
      unit: it.unit,
      estimatedPrice: it.estimatedPrice || 0,
      valorEstimadoSigiloso: it.valorEstimadoSigiloso || undefined,
      unitPrice: parseFloat(it.unitPrice) || 0,
      brand: it.brand,
      model: it.model || undefined,
      catser: it.catser || undefined,
      groupNumber: it.groupNumber || undefined,
      groupDescription: it.groupDescription || undefined,
      requiresBrandModel: it.requiresBrandModel || undefined,
      // Sem isto o backend nunca recebe de volta a flag que o scrape gravou —
      // era metade da corrida que produziu o 422 do TRT-11 (28/08/2026).
      requiresModel: it.requiresModel || undefined,
      lote: it.lote || undefined,
      // Proposta composta
      chargeType: it.chargeType || undefined,
      maxUnitPrice: it.maxUnitPrice ? parseFloat(String(it.maxUnitPrice)) || undefined : undefined,
      tipoBeneficio: it.tipoBeneficio ?? undefined,
      tipoBeneficioNome: it.tipoBeneficioNome || undefined,
      // Item julgado por MAIOR DESCONTO: o percentual é o que vai pro portal;
      // o unitPrice acima é o R$ derivado dele. O backend recalcula o derivado
      // a partir daqui (normalizeDiscountItems), então os dois nunca divergem.
      priceMode: it.priceMode === 'desconto' ? 'desconto' : undefined,
      // `!= null` e `!== ''`: 0% é oferta válida (o próprio valor estimado);
      // só o campo em branco é "não preenchido".
      discountPercent:
        it.priceMode === 'desconto' && it.discountPercent != null && it.discountPercent !== ''
          ? parseFloat(String(it.discountPercent)) || 0
          : undefined,
    }))));
  }

  if (declarations.length > 0) {
    fd.append('declarations', JSON.stringify(declarations));
  }

  // Termos do portal. BLL/BNC NÃO populam item.termsDeclarations (o scrape
  // público só pega o ME/EPP), por isso a checagem explícita por portalOrigem —
  // sem ela o meeppValue do usuário é descartado em silêncio e o portal recebe
  // IsMe=False (a IPCOM perde o benefício da LC 123/2006).
  const isBll = portalOrigem === 'bll' || portalOrigem === 'bnc';
  const acceptedTerms = buildAcceptedTermsPayload(termsAccepted, meeppValue, declarations);
  acceptedTerms.margemPreferencia = {
    paisOrigem: margemPaisOrigem || 'CHN',
    enquadramento: margemEnquadramento || '0',
  };
  if (portalOrigem === 'pcp' && pcpInfoComp.length > 0) {
    acceptedTerms.pcpInfoComplementar = pcpInfoComp;
  }
  if (hasPortalTerms || acceptedTerms.equidadeGenero || acceptedTerms.integridade || isBll || meeppValue) {
    fd.append('acceptedTerms', JSON.stringify(acceptedTerms));
  }

  if (form.validityDays) fd.append('validityDays', form.validityDays);
  fd.append('valorRecorrente', String(form.valorRecorrente));
  if (form.contractMonths) fd.append('contractMonths', form.contractMonths);
  if (form.observations) fd.append('observations', form.observations);
  if (form.conditions) fd.append('conditions', form.conditions);
  if (form.representative) fd.append('representative', form.representative);
  if (form.representativeRole) fd.append('representativeRole', form.representativeRole);
  if (form.bankInfo) fd.append('bankInfo', form.bankInfo);
  if (form.brand) fd.append('brand', form.brand);
  if (form.model) fd.append('model', form.model);
  if (form.pdfLayout) fd.append('pdfLayout', form.pdfLayout);
  fd.append('pdfIncluirTributacao', String(form.pdfIncluirTributacao));
  return fd;
}
